import { Link, useParams } from "react-router-dom";
import { getArticle } from "../lib/api";
import { useApi } from "../lib/useApi";
import { ArticleBody } from "../lib/html";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { formatDateShort } from "../lib/format";

/** A single published KB article (GET /kb/:slug). Public. Renders body_md. */
export function KbArticlePage() {
  const { slug = "" } = useParams();
  const { data, loading, error, reload } = useApi(
    (signal) => getArticle(slug, signal),
    [slug],
  );

  if (loading) return <Loading label="Loading article…" />;

  if (error) {
    // A 404 surfaces as ApiError(404); show a friendly not-found.
    const notFound = error.toLowerCase().includes("not found");
    if (notFound) {
      return (
        <EmptyState title="Article not found">
          <p className="muted">This article may have been moved or unpublished.</p>
          <Link to="/kb" className="btn small">
            Back to the knowledge base
          </Link>
        </EmptyState>
      );
    }
    return <ErrorState error={error} onRetry={reload} />;
  }

  const article = data?.article;
  if (!article) {
    return (
      <EmptyState title="Article not found">
        <Link to="/kb" className="btn small">
          Back to the knowledge base
        </Link>
      </EmptyState>
    );
  }

  return (
    <article className="article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/kb">Knowledge base</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{article.title}</span>
      </nav>

      <header className="article-header">
        <h1>{article.title}</h1>
        <p className="article-meta muted">Updated {formatDateShort(article.updated_at)}</p>
      </header>

      <div className="article-body">
        {/* Stored HTML → sanitized innerHTML; markdown content falls back to the
            React-element renderer. See lib/html.tsx for the why. */}
        <ArticleBody source={article.body_md} />
      </div>

      <footer className="article-foot">
        <p className="muted">Still need help?</p>
        <Link to="/tickets/new" className="btn">
          Submit a request
        </Link>
      </footer>
    </article>
  );
}
